/**
 * useEventSectionCounts — single source of truth for the six Events-section
 * tab counts (Events · Event Hubs · Event Templates · Tickets · Landing Pages ·
 * Archive). Every event-section screen calls this and passes the result into
 * `eventsSectionTabs`, so each tab shows the SAME total on every screen —
 * counts no longer disappear or change when you switch tabs.
 *
 * Counts read the same data sources the destination screens render from:
 *   events    → active (non-archived) events
 *   hubs      → useHubs()
 *   templates → useTemplates()
 *   tickets   → useTicketGroupCount() (v2 ticket groups; the "Tickets" tab routes to the v2 Tickets screen — 2026-10-02)
 *   landing   → useLandingPages()
 *   archived  → past (ended) events
 */
import { useEvents } from "./useEvents";
import { useHubs } from "../hubs/hubs-data";
import { useTemplates } from "../templates/useTemplates";
import { useTicketGroupCount } from "../tickets/v2/ticket-group-count";
import { useLandingPages } from "../landing/LandingPagesIndex";
import { can } from "../../shell/access";

export interface EventSectionCounts {
  events: number; hubs: number; templates: number;
  /** Undefined until the v2 group list resolves — the tab shows no count rather than a wrong one. */
  tickets: number | undefined; landing: number; archived: number;
  // Index signature so the counts object satisfies eventsSectionTabs'
  // Partial<Record<string, number>> parameter.
  [key: string]: number | undefined;
}

export function useEventSectionCounts(): EventSectionCounts {
  const { events } = useEvents();
  /* Each count is read only by a role its read allows (CC, 2026-10-04): listHubs needs hubs.read,
     listEventTemplates templates.read (templates.write also lists them), the ticket-group library
     products.read. A refused read is not a count. */
  const hubs = useHubs(can("hubs.read"));
  const { templates } = useTemplates(can("templates.read") || can("templates.write"));
  const tickets = useTicketGroupCount(can("products.read"));
  const landing = useLandingPages();
  return {
    events: events.filter((e) => e.status !== "past").length,
    hubs: hubs.length,
    templates: templates.length,
    tickets,
    landing: landing.length,
    archived: events.filter((e) => e.status === "past").length,
  };
}
